import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, X, Loader2 } from "lucide-react";
import { isNative, WEB_ORIGIN, redirectExternal } from "@/lib/native";
import { money } from "@/components/marketplace/shared";

const fmt = (d) => new Date(d).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });

export function FeaturePurchaseSheet({ open, onClose, targetType, targetId, targetName, active }) {
  const [prices, setPrices] = useState(null);
  const [mode, setMode] = useState("test");
  const [days, setDays] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/featured/prices").then((r) => { setPrices(r.data.prices); setMode(r.data.stripe_mode); setDays((p) => p || r.data.prices[targetType]?.[0]?.days || null); }).catch(() => setPrices({}));
  }, [open, targetType]);

  if (!open) return null;
  const plans = prices?.[targetType] || [];
  const plan = plans.find((p) => p.days === days);
  const start = active ? new Date(active.featured_ends_at) : new Date();
  const end = plan ? new Date(start.getTime() + plan.days * 86400000) : null;

  const pay = async () => {
    if (!plan) return;
    setBusy(true);
    try {
      const r = await api.post("/featured/checkout", { target_type: targetType, target_id: targetId, days: plan.days, origin_url: isNative ? WEB_ORIGIN : window.location.origin });
      await redirectExternal(r.data.checkout_url);
      setTimeout(() => setBusy(false), 5000);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Não foi possível iniciar o pagamento."); setBusy(false); }
  };

  return (
    <div data-testid="feature-sheet" className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden max-h-[92vh] overflow-y-auto animate-in slide-in-from-bottom-2" onClick={(e) => e.stopPropagation()}>
        <div className="bg-black text-white p-5 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-zinc-300 font-bold flex items-center gap-1"><Sparkles size={12} /> {active ? "Prolongar destaque" : "Destacar"}</div>
            <div className="font-display font-bold mt-1 text-lg leading-tight truncate">{targetName}</div>
          </div>
          <button data-testid="feature-sheet-close" onClick={onClose} className="text-white/70 hover:text-white shrink-0"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-zinc-500">Escolhe a duração. {active ? "Os dias somam-se à data de fim atual." : "O destaque começa assim que o pagamento for confirmado."}</p>
          {!prices && <p className="text-sm text-zinc-500 text-center py-4">A carregar preços…</p>}
          {prices && plans.length === 0 && <p className="text-sm text-zinc-500 text-center py-4">Destaques indisponíveis para este tipo de conteúdo.</p>}
          <div className="grid grid-cols-3 gap-2">
            {plans.map((p) => (
              <button key={p.days} data-testid={`feature-plan-${p.days}`} onClick={() => setDays(p.days)} data-active={days === p.days}
                      className="rounded-xl border border-zinc-200 p-3 text-center hover:border-black transition-colors data-[active=true]:border-black data-[active=true]:bg-zinc-50">
                <div className="font-bold text-sm">{p.days} dias</div>
                <div className="font-display font-extrabold text-base mt-0.5">{money(p.amount_minor, "GBP")}</div>
              </button>
            ))}
          </div>
          {plan && (
            <div data-testid="feature-summary" className="rounded-xl border border-zinc-200 divide-y divide-zinc-100 text-sm">
              <Row k="Conteúdo" v={targetName} />
              <Row k="Duração" v={`${plan.days} dias`} />
              <Row k={active ? "Início (após o atual)" : "Início previsto"} v={fmt(start)} />
              <Row k="Fim" v={fmt(end)} />
              <Row k="Total" v={<span className="font-display font-extrabold">{money(plan.amount_minor, "GBP")}</span>} />
            </div>
          )}
          <button data-testid="feature-pay-btn" disabled={!plan || busy} onClick={pay}
                  className="w-full py-3 rounded-xl bg-black text-white text-sm font-bold hover:bg-[#c8102e] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Pagar e destacar
          </button>
          {mode === "test" && <p data-testid="feature-test-hint" className="text-[10px] text-zinc-400 text-center">Ambiente de teste — cartão 4242 4242 4242 4242 · qualquer data futura · qualquer CVC</p>}
          <p className="text-[11px] text-zinc-500 leading-relaxed pt-2 border-t border-zinc-100">
            O destaque dá mais visibilidade durante o período escolhido e ativa só após confirmação do pagamento. Não garante vendas, contactos ou visualizações, não é uma aprovação nem um selo oficial e não renova automaticamente. Consulta a <Link to="/reembolsos" className="underline font-semibold">Política de Reembolsos</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex items-center justify-between gap-3 px-3 py-2"><span className="text-zinc-500 text-xs">{k}</span><span className="text-right truncate">{v}</span></div>;
}
